/**
 * Which moments celebrate, how loudly, and how a notification maps onto one.
 * Pure: no React, no DOM, no storage — the bus, seen store and layer build on
 * this. The key is shared by the actor-side celebrate() call and the
 * counterparty's notification, so one event only ever celebrates once.
 */
export type CelebrationKind = "quest_completed" | "task_approved" | "hired" | "quest_published";

export interface CelebrationPreset {
  intensity: "big" | "medium" | "small";
  particleCount: number;
  /** Chime arpeggio, Hz. */
  notes: number[];
}

export const CELEBRATION_PRESETS: Record<CelebrationKind, CelebrationPreset> = {
  quest_completed: {
    intensity: "big",
    particleCount: 160,
    notes: [523.25, 659.25, 783.99, 1046.5],
  },
  task_approved: {
    intensity: "medium",
    particleCount: 90,
    notes: [659.25, 783.99, 1046.5],
  },
  hired: {
    intensity: "medium",
    particleCount: 90,
    notes: [587.33, 739.99, 880],
  },
  quest_published: {
    intensity: "small",
    particleCount: 50,
    notes: [783.99, 1046.5],
  },
};

const RANK = { big: 3, medium: 2, small: 1 } as const;

/** Higher wins when several celebrations compete for one slot. */
export function celebrationRank(kind: CelebrationKind): number {
  return RANK[CELEBRATION_PRESETS[kind].intensity];
}

export interface Celebration {
  kind: CelebrationKind;
  entityId: string;
  key: string;
}

export function celebrationKey(kind: CelebrationKind, entityId: string): string {
  return `${kind}:${entityId}`;
}

/**
 * Notification types written by the DB triggers for the COUNTERPARTY.
 * Deliberately absent: quest_posted (the creator's echo of their own publish),
 * quest_funds_locked (always follows a hire — would celebrate twice),
 * quest_created_admin.
 */
const NOTIFICATION_KINDS: Record<string, { kind: CelebrationKind; entity: "quest" | "task" }> = {
  quest_completed: { kind: "quest_completed", entity: "quest" },
  payment_released: { kind: "task_approved", entity: "task" },
  proposal_accepted: { kind: "hired", entity: "task" },
};

/**
 * Written only to the worker whose task was approved — never the creator, who
 * also receives quest_completed. A batch holding one means the viewer's job
 * just ended, whichever celebration wins the slot.
 */
const WORKER_JOB_ENDED_TYPES = new Set(["payment_released"]);

const QUEST_ID = /^\/quest\/([^/?#]+)/;
const TASK_ID = /^\/quest\/[^/?#]+\/task\/([^/?#]+)/;

/**
 * Structural, not `Pick<Notification>`: the rules must accept any stored type
 * string (the DB can write types the TS union doesn't list yet) and a null
 * link, and a real Notification still satisfies it.
 */
export interface CelebrationCandidate {
  type: string;
  linkTo: string | null;
  read: boolean;
  createdAt: string;
}

export function celebrationForNotification(
  n: Pick<CelebrationCandidate, "type" | "linkTo">,
): Celebration | null {
  const rule = NOTIFICATION_KINDS[n.type];
  if (!rule || typeof n.linkTo !== "string") return null;
  const match = (rule.entity === "quest" ? QUEST_ID : TASK_ID).exec(n.linkTo);
  const entityId = match?.[1];
  if (!entityId) return null;
  return {
    kind: rule.kind,
    entityId,
    key: celebrationKey(rule.kind, entityId),
  };
}

/**
 * At most ONE celebration per batch — a worker opening the app to three
 * approvals gets one burst, not a volley. Every candidate is returned in
 * `consumed` so the runner-up doesn't fire on the next poll.
 */
export function pickCelebration(
  notifications: CelebrationCandidate[],
  seen: ReadonlySet<string>,
  watermark: string,
): {
  celebration: Celebration | null;
  consumed: string[];
  workerJobEnded: boolean;
  /** The quest whose job ended — where the worker's next step is asked. */
  workerQuestId: string | null;
} {
  const wm = Date.parse(watermark);
  const consumed = new Set<string>();
  let best: Celebration | null = null;
  let workerJobEnded = false;
  let workerQuestId: string | null = null;
  for (const n of notifications) {
    if (n.read || !(Date.parse(n.createdAt) > wm)) continue;
    const c = celebrationForNotification(n);
    if (!c || seen.has(c.key) || consumed.has(c.key)) continue;
    consumed.add(c.key);
    if (WORKER_JOB_ENDED_TYPES.has(n.type)) {
      workerJobEnded = true;
      workerQuestId ??= QUEST_ID.exec(n.linkTo ?? "")?.[1] ?? null;
    }
    if (!best || celebrationRank(c.kind) > celebrationRank(best.kind)) best = c;
  }
  return { celebration: best, consumed: [...consumed], workerJobEnded, workerQuestId };
}
